import { Paper, Stack, Popover } from "@mui/material";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import React, { useState } from "react";
import { useImmer } from "use-immer";
import { ValidateNIP } from "../../engine/baseApiClient";
import { getCountries } from "../../utility/countries";
import NipGusComponent from "./nipGusComponent";
import { alpha } from "@mui/material"; 
import { useSelector } from "react-redux";
import { selectLayoutSettings } from "features/user/userSlice";
import { useMaterialUIController } from "context";
import BillzTypographyTitle from "components/BillzTypographies/BillzTypographyTitle";
import { Select } from "components/ui";
import { Collapse } from '@mui/material';
import { Checkbox } from '../ui/Checkbox/Checkbox'
import * as Styled from './editContractorPanel.styles';

export default function EditContractorPanel(props) {
    const id = props.id ?? 'editContractorPanel';

    const onContractorChanged = props.onContractorChanged;
    const searchEnabled = props.searchEnabled ?? false;

    const isError = (obj) => { return obj === false; }

    const [data, setData] = useImmer(props.data);
    const [userId, setUserId] = useState(null);
    const [showMore, setShowMore] = useState(false);

    const [validationData, setValidationData] = useImmer({
        name: undefined,
        nip: {
            nip: undefined
        },
        address: {
            country: undefined,
            city: undefined,
            street: undefined,
            houseNumber: undefined,
            zipCode: undefined
        },
        temporary: true,
    });

    const open = Boolean(props.anchorEl);
    
    const layoutSettings = useSelector(selectLayoutSettings);
    
    const [controller] = useMaterialUIController();
    const {
      darkMode,
    } = controller;

    //#region data validation

    const isInvalid = (obj)=>{ return obj === false || obj === undefined; }
    
    const validationDataValueState = (validationState, newValue) => { 
        if(validationState === undefined && (newValue === '' || newValue === undefined)) {
            return undefined;
        } else {
            return (newValue !== '');
        }
    }

    const validationDataNipValueState = async (validationState, country, nip) => { 
        const newValidationState = validationDataValueState(validationState, nip);

        if(newValidationState === undefined || newValidationState === false) {
            return {result: newValidationState};
        }

        return (await ValidateNIP(nip, country));
    }

    const updateValidationDataProperty = (property, value) => {
        switch(property){
            case 'name': setValidationData(draft => {draft.name = value; }); return;
            case 'nip': setValidationData(draft => {draft.nip.nip = value; }); return;
            case 'street': setValidationData(draft => {draft.address.street = value; }); return;
            case 'houseNumber': setValidationData(draft => {draft.address.houseNumber = value; }); return;
            case 'zipCode': setValidationData(draft => {draft.address.zipCode = value; }); return;
            case 'city': setValidationData(draft => {draft.address.city = value; }); return;
            case 'country': setValidationData(draft => {draft.address.country = value; }); return;
        }
    }

    const validateObj = (obj, invalidProps, updateValidationData) => {       
        var isValid = true;
       
        for (var prop in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, prop)) {
                if(obj[prop] !== undefined && Object.keys(obj[prop]).length ){                
                    invalidProps[prop] = {};
                    isValid = isValid & validateObj(obj[prop], invalidProps[prop], updateValidationData);
                    continue;
                }
                if(isInvalid(obj[prop]))
                {
                    console.log(prop);
                    updateValidationDataProperty(prop, false);
                    invalidProps[prop] = false;
                    isValid = false;
                }
                else
                {
                    invalidProps[prop] = true; 
                }
            }
        }

        return isValid;
    }

    const isContractorValid = async () => {
        var isValid = true;
        var invalidProps = {};
        isValid = await validateObj(validationData, invalidProps, true);
        return isValid;
    }

    //#endregion

    //#region buttons click events

    const saveContractorEditClick = async (event) => {
        if(!(await isContractorValid())) {               
            return; 
        }
        onContractorChanged(event, data, userId)
        props.onCloseClick(event);
    }

    const cancelContractorEditClick = (event) => {
        setData(props.data);

        onValidateName(props.data.name);
        onValidateAddressStreet(props.data.address.street);
        onValidateAddressHouseNumber(props.data.address.houseNumber);
        onValidateAddressZipCode(props.data.address.zipCode);
        onValidateAddressCity(props.data.address.city);
        onValidateAddressCountry(props.data.address.country);
        onValidateNip(props.data.address.country, props.data.nip.nip);

        props.onCloseClick(event);        
    }

    //#endregion

    //#region contractor whole object setter handlers

    const onContractorFound = (event, newValue, id) => {
        onValidateName(newValue.name);
        onValidateAddressStreet(newValue.address.street);
        onValidateAddressHouseNumber(newValue.address.houseNumber);
        onValidateAddressZipCode(newValue.address.zipCode);
        onValidateAddressCity(newValue.address.city);
        onValidateAddressCountry(newValue.address.country);
        onValidateNip(newValue.address.country, newValue.nip.nip);

        setData(newValue);
        setUserId(id);
    }

    //#endregion

    //#region properies onChanged events

    const onNameChanged = (newValue) => {
        onValidateName(newValue);
        setData(draft => { draft.name = newValue; });
    }

    const onValidateName = (value) => {
        setValidationData(draft => { draft.name = validationDataValueState(draft.name, value); });
    }

    const onNipChanged = async (newValue) => {
        setData(draft => { draft.nip.nip = newValue; });        
        await onValidateNip('PL', newValue);
    }

    const onValidateNip = async (country, nip) => {
        if(data.isCompany == false) setValidationData(draft => { draft.nip.nip = true; });
        else {
            const validationResult = (await validationDataNipValueState(validationData.nip.nip, 'PL', nip))?.result;
            setValidationData(draft => { draft.nip.nip = validationResult; });
        }
    }

    const onAddressStreetChanged = (newValue) => {
        onValidateAddressStreet(newValue);
        setData(draft => { draft.address.street = newValue; });
    }

    const onValidateAddressStreet = (value) => {
        setValidationData(draft => { draft.address.street = validationDataValueState(draft.address.street, value); });
    }

    const onAddressHouseNumberChanged = (newValue) => {
        onValidateAddressHouseNumber(newValue);
        setData(draft => { draft.address.houseNumber = newValue; });
    }

    const onValidateAddressHouseNumber = (value) => {
        setValidationData(draft => { draft.address.houseNumber = validationDataValueState(draft.address.houseNumber, value); });
    }

    const onAddressApartmentNumberChanged = (newValue) => {
        setData(draft => { draft.address.apartmentNumber = newValue; });
    }

    const onAddressZipCodeChanged = (newValue) => {
        onValidateAddressZipCode(newValue);
        setData(draft => { draft.address.zipCode = newValue; });
    }

    const onValidateAddressZipCode = (value) => {
        setValidationData(draft => { draft.address.zipCode = validationDataValueState(draft.address.zipCode, value); });
    }

    const onAddressCityChanged = (newValue) => {
        onValidateAddressCity(newValue);
        setData(draft => { draft.address.city = newValue; });
    }

    const onValidateAddressCity = (value) => {
        setValidationData(draft => { draft.address.city = validationDataValueState(draft.address.city, value); });
    }

    const onAddressCountryChanged = async (newValue) => {
        setData(draft => { draft.address.country = newValue; });

        await onValidateNip(newValue, data.nip.nip);
        onValidateAddressCountry(newValue);
    }

    const onValidateAddressCountry = (value) => {
        setValidationData(draft => { draft.address.country = validationDataValueState(draft.address.country, value); });
    }

    const onEmailChanged = (event) => {
        setData(draft => { draft.email = event.target.value; });
    }

    const onPhoneChanged = (event) => {
        setData(draft => { draft.phone = event.target.value; });
    }

    const onCellPhoneChanged = (event) => {
        setData(draft => { draft.cellPhone = event.target.value; });
    }

    const onIsCompanyChanged = (value) => {
        setData(draft => { draft.isCompany = value; });
    }

    const onCommuneChanged = (event) => {
        setData(draft => { draft.address.commune = event.target.value; });
    }

    const onCountyChanged = (event) => {
        setData(draft => { draft.address.county = event.target.value; });
    }

    const onVoivodeshipChanged = (event) => {
        setData(draft => { draft.address.voivodeship = event.target.value; });
    }

    const onCollapseButtonClicked = (event) => {
        setShowMore(!showMore);
    }

    //#endregion

    React.useEffect(() => {       
         setData(props.data);
         setData(draft => { draft.temporary = false; });    // disables temporary after 1st popup shows
         onValidateName(props.data.name);
         onValidateAddressStreet(props.data.address.street);
         onValidateAddressHouseNumber(props.data.address.houseNumber);
         onValidateAddressZipCode(props.data.address.zipCode);
         onValidateAddressCity(props.data.address.city);
         onValidateAddressCountry(props.data.address.country);
    }, [props.data]);

    React.useEffect(() => {
        onValidateNip(data.address.country, data.nip.nip);
    }, [data.nip.nip, data.address.country, data.isCompany])

    return (
        <Popover open={ open } 
            anchorEl={ props.anchorEl } 
            anchorOrigin={{ vertical: 'top', horizontal: 'left', }} 
            transformOrigin={{ vertical: 'top', horizontal: 'left', }}
            elevation={10}            
            sx={{
                backgroundColor: (theme) => {
                    return darkMode ? alpha(theme.palette.light.main, 0.5) : alpha(theme.palette.dark.main, 0.5);
                } 
            }}
            PaperProps={{
                sx:{backgroundColor:'transparent', boxShadow:0, borderRadius:3}
            }}
        >
        <Paper 
        sx={{ p:2, borderRadius:3,
            backgroundColor: (theme) => {
                return darkMode ? theme.palette.dark.main : theme.palette.light.main;
            } 
        }} variant='outlined'>            
                <Stack spacing={1.5}>
                    <BillzTypographyTitle color='text' fontSize={20} sx={{mb:1}}>{props.title}</BillzTypographyTitle>
                    <Collapse in={data.isCompany}>
                        <NipGusComponent 
                            id={id+'-nip'} 
                            width={400} 
                            onContractorDataSet={onContractorFound}
                            onValidationChanged={(e) => {}}
                            onValueChanged={(value) => onNipChanged(value)}
                            searchEnabled={searchEnabled}
                            value={data.nip.nip}
                            error={validationData.nip.nip}
                        />
                    </Collapse>

                    <Checkbox
                        label='Kontrahent nie ma nadanego numeru NIP'
                        id="contractor-is-person-checkbox"
                        size="small"
                        onChange={(checked) => onIsCompanyChanged(!checked)}
                        checked={!data.isCompany}
                    />

                    <MDInput id={id+'-name'} size="small" title="Nazwa" required label="Nazwa" InputProps={{ sx: { width: 400 } }} error={ isError(validationData.name)} value={data.name} onChange={(e) => onNameChanged(e.target.value)}/>

                    <Stack direction="row" spacing={1}>
                        <MDInput id={id+'-street'} size="small" title="Ulica" required label="Ulica" InputProps={{ sx: { width: 225 } }} error={isError(validationData.address.street) } value={data.address.street} onChange={(e) => onAddressStreetChanged(e.target.value)}/>
                        <MDInput id={id+'-house'} size="small" required title="Dom" label="Dom" InputProps={{ sx: { width: 80 } }} error={ isError(validationData.address.houseNumber)} value={data.address.houseNumber} onChange={(e) => onAddressHouseNumberChanged(e.target.value)}/>
                        <MDInput id={id+'-flat'} size="small" label="Lokal" title="Lokal" InputProps={{ sx: { width: 80 } }}  value={data.address.apartmentNumber} onChange={(e) => onAddressApartmentNumberChanged(e.target.value)}/>
                    </Stack>

                    <Stack direction="row" spacing={1}>
                        <MDInput id={id+'-zipCode'} size="small" required title="Kod pocztowy" label="Kod pocztowy" InputProps={{ sx: { width: 140 } }}  error= {isError(validationData.address.zipCode)} value={data.address.zipCode} onChange={(e) => onAddressZipCodeChanged(e.target.value)}/>
                        <MDInput id={id+'-city'} size="small" required title="Miejscowość" label="Miejscowość" InputProps={{ sx: { width: 255 } }}  error= { isError(validationData.address.city)} value={data.address.city} onChange={(e) => onAddressCityChanged(e.target.value)}/>
                    </Stack>

                    <Select required id={id+'-country'} label="Kraj" options={getCountries()} error={ isError(validationData.address.country)} value={data.address.country} onChange={(value) => onAddressCountryChanged(value)} />
                    <MDInput size="small" id={id+'-email'} label="Email" title="Email" InputProps={{ sx: { width: 400 } }} value={data.email} onChange={onEmailChanged}/>
                    <MDInput size="small" id={id+'-phone'} label="Telefon" title="Telefon" InputProps={{ sx: { width: 400 } }} value={data.phone} onChange={onPhoneChanged}/>
                    <MDInput size="small" id={id+'-cellPhone'} label="Telefon komórkowy" title="Telefon komórkowy" InputProps={{ sx: { width: 400 } }} value={data.cellPhone} onChange={onCellPhoneChanged}/>                              
                    
                    <Collapse in={showMore}>
                        <Stack spacing={1.5}>
                            <MDInput size="small" id={id+'-commune'} label="Gmina" title="Gmina" InputProps={{ sx: { width: 400 } }} value={data.address.commune} onChange={onCommuneChanged}/>
                            <MDInput size="small" id={id+'-county'} label="Powiat" title="Powiat" InputProps={{ sx: { width: 400 } }} value={data.address.county} onChange={onCountyChanged}/>
                            <MDInput size="small" id={id+'-voivodeship'} label="Województwo" title="Województwo" InputProps={{ sx: { width: 400 } }} value={data.address.voivodeship} onChange={onVoivodeshipChanged}/>
                        </Stack>
                    </Collapse>

                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{pt:2}}>
                        <MDButton id={id+'-cancel-button'} variant="outlined" color="info" size='small' sx={{width: 100}} onClick={cancelContractorEditClick}>Anuluj</MDButton>
                        <MDButton id={id+'-save-button'} variant="contained" color="info" size='small' sx={{width: 100}} onClick={saveContractorEditClick}>Zapisz</MDButton>
                    </Stack>
                </Stack>
            
            </Paper>
        </Popover>
    )
}